import { Inject, Injectable, InjectionToken } from '@angular/core';

/**
 * Build-time, per-region settings for routing opted-in users to the V6 experience.
 *
 * They come from the SSM parameters `V6_LEADS_ENABLED`, `V6_BASE_URL` and `V6_COOKIE_PREFIX`, read when the image is built.
 * Changing a parameter has no effect until a new build is deployed; without them the redirect stays off.
 */
export interface V6ExperienceConfig {
  /** Build-time kill switch. When false the preference cookie is ignored. */
  leadsEnabled: boolean;
  /** Base URL of the V6 app for this region, e.g. https://gjs.gjgardner.com.au */
  baseUrl: string;
  /** Environment-scoped cookie prefix; must match V6's `optIn.cookiePrefix`. */
  cookiePrefix: string;
}

export const V6_EXPERIENCE_CONFIG = new InjectionToken<V6ExperienceConfig>('V6_EXPERIENCE_CONFIG');

/** A session's standing for the V6 hand-over: whether it may go at all, and the office V6 must open it in. */
export interface SessionHandoff {
  /** False keeps the user in classic Leads. */
  eligible: boolean;
  /** The office to put V6 in (`?scopeOffice=`), or `null` when V6's own default for the user is already right. */
  officeId: string | null;
  /**
   * True for an office user in their own office, who may hold access to several offices (multi-office login). V6 opens
   * them in their home office whatever they picked here, so they go with {@link resolveOfficeHandoff}'s answer.
   */
  checkMultiOffice: boolean;
  /**
   * True for a corporate or master user in their own home view. They have no office, and V6 would reopen them on the last
   * scope they picked there, so they are sent with `?scopeHome=1` to land on all their offices.
   */
  home: boolean;
}

/** How an office user with these accessible offices goes to V6: not at all, or in which office. */
export interface OfficeHandoff {
  go: boolean;
  /** The office V6 must open in (`?scopeOffice=`); `null` when the user has one office and V6's default is right. */
  officeId: string | null;
}

/**
 * Whether, and in which office, an office user may be sent to V6.
 *
 * V6 opens an employee in their home office unless told otherwise, so a user with several offices is sent with the office
 * they are working in, provided it is one of the offices they may enter. Without a usable active office they stay in classic
 * Leads rather than land in the wrong office's leads.
 */
export function resolveOfficeHandoff(
  accessibleOffices: ReadonlyArray<{ officeId: string | number }>,
  activeOfficeId: string | number | null | undefined,
): OfficeHandoff {
  if (accessibleOffices.length <= 1) {
    return { go: true, officeId: null };
  }
  const active = activeOfficeId === null || activeOfficeId === undefined ? '' : String(activeOfficeId);
  const listed = accessibleOffices.some((office) => String(office.officeId) === active);
  return listed && SCOPE_OFFICE_ID.test(active) ? { go: true, officeId: active } : { go: false, officeId: null };
}

/** The shape V6 accepts for an office id (its `OFFICE_ID_PATTERN`); anything else is not handed over. */
const SCOPE_OFFICE_ID = /^[A-Za-z0-9]{1,64}$/;

/**
 * Whether the current session may be sent to V6, and in which office.
 *
 * - An office user in their OWN office: eligible, nothing to hand over, V6 opens them in their home office. They must also
 *   pass the multi-office check (`checkMultiOffice`).
 * - A corporate or master user in their OWN home view (network / zone): eligible, and V6 is told to open them on all their
 *   offices (`home`, sent as `?scopeHome=1`), because it would otherwise reopen the last office they picked there.
 * - A corporate or master user who switched into an office (`office-view/<id>/...`): eligible, and V6 is told the office
 *   (V6's Switch office control, GJGSAM-2758, can show "this office"). With no usable office id they stay classic rather
 *   than land somewhere else.
 * - A corporate user switched into a master zone (`master-view/<id>/...`): stays classic. V6 is not handed a zone yet.
 *
 * `switched` comes from the user context rather than the URL, because the `office-view` / `master-view` prefix is a base
 * href the router strips before it sees the URL.
 */
export function resolveSessionHandoff(session: {
  switched: boolean;
  officeView: boolean;
  officeId?: string | number | null;
}): SessionHandoff {
  if (!session.switched) {
    return { eligible: true, officeId: null, checkMultiOffice: session.officeView, home: !session.officeView };
  }
  const officeId = session.officeId === null || session.officeId === undefined ? '' : String(session.officeId);
  return session.officeView && SCOPE_OFFICE_ID.test(officeId)
    ? { eligible: true, officeId, checkMultiOffice: false, home: false }
    : { eligible: false, officeId: null, checkMultiOffice: false, home: false };
}

/** Where to send a corporate or master user who lands on an office-only classic Leads URL. */
export interface ClassicLanding {
  /** App-relative URL (router URL, or an `/office-view/<id>/...` path when `fullPageLoad` is true). */
  url: string;
  /** True when the target is under another base href (`office-view/<id>/`), which needs a full page load. */
  fullPageLoad: boolean;
}

const SAFE_ID = /^[A-Za-z0-9_-]+$/;

/**
 * Where a corporate or master user (in their own home view) should land when they arrive at `/sales/leads...`, which only
 * exists in office view. They arrive there from V6's "Switch to classic" and its kill-switch bounce, which always map a V6
 * Leads URL to `/sales/leads...` because V6 does not know the user type at that point.
 *
 * - list and board -> their own Leads page, `/leads` (query dropped: it is V6 filter state, meaningless there)
 * - a lead -> that lead in its office, `/office-view/<office>/sales/leads/<id>`, when V6 sent the office along
 *   (`?office=<id>`, the same query V6 uses to open a lead in another office); otherwise their Leads page
 * - anything else (including create) -> `null`, left alone
 */
export function resolveAdminClassicLanding(url: string): ClassicLanding | null {
  const suffixStart = url.search(/[?#]/);
  const rawPath = suffixStart < 0 ? url : url.substring(0, suffixStart);
  const path = rawPath.length > 1 ? rawPath.replace(/\/+$/, '') : rawPath;

  if (path === LEADS_LIST_PATH || path === LEADS_BOARD_PATH) {
    return { url: LEADS_ADMIN_LIST_PATH, fullPageLoad: false };
  }

  const details = LEAD_DETAILS_PATH.exec(path);
  if (!details || path === LEADS_NEW_PATH) {
    return null;
  }

  const query = suffixStart < 0 ? '' : url.substring(suffixStart).split('#')[0];
  const office = new URLSearchParams(query).get('office');
  return office && SAFE_ID.test(office) && SAFE_ID.test(details[1])
    ? { url: `/office-view/${office}/sales/leads/${details[1]}`, fullPageLoad: true }
    : { url: LEADS_ADMIN_LIST_PATH, fullPageLoad: false };
}

/**
 * Whether a navigation is the user moving through browser history (Back / Forward) rather than choosing to go somewhere.
 *
 * Never redirect these. Pressing Back from V6 lands on an earlier classic Leads entry, and redirecting it sends the user
 * straight back to V6, so Back appears not to work. Covers both ways history is traversed: an in-app `popstate`, and a full
 * page load that the browser reports as `back_forward` (only meaningful for the first navigation of that page load).
 */
export function isHistoryTraversal(
  navigationTrigger: string | undefined,
  isInitialNavigation: boolean,
  documentLoadType: string | undefined,
): boolean {
  return navigationTrigger === 'popstate' || (isInitialNavigation && documentLoadType === 'back_forward');
}

/** Mirrors V6's `SCOPE_OFFICE_PARAM`. Not `office`, which is already V6's Office filter on the list and board. */
const SCOPE_OFFICE_PARAM = 'scopeOffice';

/** Mirrors V6's `SCOPE_HOME_PARAM`: `scopeHome=1` means "all my offices" (zone for a master, network for corporate). */
const SCOPE_HOME_PARAM = 'scopeHome';

/** Sets one param on a `?query#fragment` suffix, replacing any the incoming URL already carried. */
function withParam(suffix: string, name: string, value: string): string {
  const hashStart = suffix.indexOf('#');
  const fragment = hashStart < 0 ? '' : suffix.substring(hashStart);
  const params = new URLSearchParams(hashStart < 0 ? suffix : suffix.substring(0, hashStart));
  params.set(name, value);
  return `?${params.toString()}${fragment}`;
}

const LEADS_COOKIE_KEY = 'leads';
const OPTED_IN_VALUE = '1';

const LEADS_LIST_PATH = '/sales/leads';
/** The classic Leads page of corporate and master users (their menu's Leads item). */
const LEADS_ADMIN_LIST_PATH = '/leads';
const LEADS_BOARD_PATH = '/sales/leads-board';
const LEADS_NEW_PATH = '/sales/leads/new';
const LEAD_DETAILS_PATH = /^\/sales\/leads\/([^/();]+)$/;

/**
 * Decides whether a classic (V5) navigation should be handed over to the V6 experience.
 *
 * The preference cookie is written and cleared only by V6, V5 just reads it. Shared contract:
 * `{cookiePrefix}leads=1`. The service is pure: it never touches the router, the window location
 * or the cookie jar beyond reading `document.cookie`.
 */
@Injectable()
export class V6ExperienceService {
  constructor(@Inject(V6_EXPERIENCE_CONFIG) private readonly config: V6ExperienceConfig) {
  }

  /**
   * Returns the absolute V6 URL for a classic URL, or `null` when the navigation has to stay in V5.
   *
   * @param url router URL (path, query and fragment), e.g. `/sales/leads/123?tab=1`
   * @param cookies raw cookie header, defaults to `document.cookie`
   * @param scopeOfficeId office V6 must open in (see {@link resolveSessionHandoff}); sent as `?scopeOffice=`
   * @param scopeHome true to have V6 open on the user's home scope (all offices); sent as `?scopeHome=1`
   */
  resolveLeadsRedirect(
    url: string,
    cookies: string = document.cookie,
    scopeOfficeId: string | null = null,
    scopeHome: boolean = false,
  ): string | null {
    if (!this.isLeadsPreferenceOn(cookies)) {
      return null;
    }

    const suffixStart = url.search(/[?#]/);
    const path = suffixStart < 0 ? url : url.substring(0, suffixStart);
    const suffix = suffixStart < 0 ? '' : url.substring(suffixStart);

    const v6Path = this.mapLeadsPath(path.length > 1 ? path.replace(/\/+$/, '') : path);
    if (v6Path === null) {
      return null;
    }

    const scoped = scopeOfficeId
      ? withParam(suffix, SCOPE_OFFICE_PARAM, scopeOfficeId)
      : scopeHome ? withParam(suffix, SCOPE_HOME_PARAM, '1') : suffix;
    return `${this.config.baseUrl.replace(/\/+$/, '')}${v6Path}${scoped}`;
  }

  /**
   * `leadsEnabled` beats the cookie. A build without a V6 base URL or cookie prefix is inert.
   */
  private isLeadsPreferenceOn(cookies: string): boolean {
    const { leadsEnabled, baseUrl, cookiePrefix } = this.config;
    if (!leadsEnabled || !baseUrl || !cookiePrefix) {
      return false;
    }

    const name = `${cookiePrefix}${LEADS_COOKIE_KEY}`;
    return cookies
      .split(';')
      .map((pair) => pair.trim())
      .some((pair) => pair === `${name}=${OPTED_IN_VALUE}`);
  }

  private mapLeadsPath(path: string): string | null {
    switch (path) {
      case LEADS_LIST_PATH:
      case LEADS_ADMIN_LIST_PATH:
        return '/leads';
      case LEADS_BOARD_PATH:
        return '/pipeline';
      case LEADS_NEW_PATH:
        return null;
      default: {
        const details = LEAD_DETAILS_PATH.exec(path);
        return details ? `/leads/${details[1]}` : null;
      }
    }
  }
}
